import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Bell, Mail, MessageSquare, Moon, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';

const phoneSchema = z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number format').optional().or(z.literal(''));
const emailSchema = z.string().email('Invalid email address').optional().or(z.literal(''));

interface AlertPreferences {
  notify_critical: boolean;
  notify_warning: boolean;
  notify_info: boolean;
  notify_staff: boolean;
  notify_service: boolean;
  notify_building: boolean;
  notify_system: boolean;
  notify_in_app: boolean;
  notify_email: boolean;
  notify_sms: boolean;
  email_address: string;
  phone_number: string;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
}

export default function AlertNotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<AlertPreferences>({
    notify_critical: true,
    notify_warning: true,
    notify_info: false,
    notify_staff: true,
    notify_service: true,
    notify_building: true,
    notify_system: true,
    notify_in_app: true,
    notify_email: false,
    notify_sms: false,
    email_address: '',
    phone_number: '',
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
  });

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('alert_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading preferences:', error);
        toast.error('Failed to load notification preferences');
        return;
      }

      if (data) {
        setPreferences({
          notify_critical: data.notify_critical,
          notify_warning: data.notify_warning,
          notify_info: data.notify_info,
          notify_staff: data.notify_staff,
          notify_service: data.notify_service,
          notify_building: data.notify_building,
          notify_system: data.notify_system,
          notify_in_app: data.notify_in_app,
          notify_email: data.notify_email,
          notify_sms: data.notify_sms,
          email_address: data.email_address || '',
          phone_number: data.phone_number || '',
          quiet_hours_enabled: data.quiet_hours_enabled,
          quiet_hours_start: data.quiet_hours_start || '22:00',
          quiet_hours_end: data.quiet_hours_end || '08:00',
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save preferences');
        return;
      }

      // Validate email and phone if notification methods are enabled
      if (preferences.notify_email && preferences.email_address) {
        const emailValidation = emailSchema.safeParse(preferences.email_address);
        if (!emailValidation.success) {
          toast.error('Please enter a valid email address');
          setSaving(false);
          return;
        }
      }

      if (preferences.notify_sms && preferences.phone_number) {
        const phoneValidation = phoneSchema.safeParse(preferences.phone_number);
        if (!phoneValidation.success) {
          toast.error('Please enter a valid phone number (e.g., +1234567890)');
          setSaving(false);
          return;
        }
      }

      const { error } = await supabase
        .from('alert_preferences')
        .upsert({
          user_id: user.id,
          ...preferences,
        });

      if (error) throw error;

      toast.success('Notification preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">Loading preferences...</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-w-3xl">
      <div>
        <h2 className="text-xl font-semibold">Notification Settings</h2>
        <p className="text-xs text-muted-foreground">
          Configure how you receive alerts
        </p>
      </div>

      {/* Alert Type Preferences */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Bell className="h-4 w-4" />
            Alert Types
          </CardTitle>
          <CardDescription className="text-xs">
            Choose which severity levels to receive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 py-3">
          <div className="flex items-center justify-between py-1.5">
            <div className="space-y-0">
              <Label htmlFor="critical" className="text-sm">Critical Alerts</Label>
              <p className="text-xs text-muted-foreground">
                Urgent issues requiring immediate attention
              </p>
            </div>
            <Switch
              id="critical"
              checked={preferences.notify_critical}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, notify_critical: checked })
              }
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-1.5">
            <div className="space-y-0">
              <Label htmlFor="warning" className="text-sm">Warning Alerts</Label>
              <p className="text-xs text-muted-foreground">
                Issues that need attention soon
              </p>
            </div>
            <Switch
              id="warning"
              checked={preferences.notify_warning}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, notify_warning: checked })
              }
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-1.5">
            <div className="space-y-0">
              <Label htmlFor="info" className="text-sm">Info Alerts</Label>
              <p className="text-xs text-muted-foreground">
                General information and updates
              </p>
            </div>
            <Switch
              id="info"
              checked={preferences.notify_info}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, notify_info: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Alert Category Preferences */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Alert Categories</CardTitle>
          <CardDescription className="text-xs">
            Choose which types of alerts to receive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 py-3">
          <div className="flex items-center justify-between py-1">
            <Label htmlFor="staff" className="text-sm">Staff Alerts</Label>
            <Switch
              id="staff"
              checked={preferences.notify_staff}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, notify_staff: checked })
              }
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-1">
            <Label htmlFor="service" className="text-sm">Service Alerts</Label>
            <Switch
              id="service"
              checked={preferences.notify_service}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, notify_service: checked })
              }
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-1">
            <Label htmlFor="building" className="text-sm">Building Alerts</Label>
            <Switch
              id="building"
              checked={preferences.notify_building}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, notify_building: checked })
              }
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-1">
            <Label htmlFor="system" className="text-sm">System Alerts</Label>
            <Switch
              id="system"
              checked={preferences.notify_system}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, notify_system: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Notification Methods */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <MessageSquare className="h-4 w-4" />
            Notification Methods
          </CardTitle>
          <CardDescription className="text-xs">
            Choose how to receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 py-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="in-app">In-App Notifications</Label>
            <Switch
              id="in-app"
              checked={preferences.notify_in_app}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, notify_in_app: checked })
              }
            />
          </div>
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="email">Email Notifications</Label>
              <Switch
                id="email"
                checked={preferences.notify_email}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, notify_email: checked })
                }
              />
            </div>
            {preferences.notify_email && (
              <div className="pl-4">
                <Label htmlFor="email-address" className="text-sm">
                  Email Address
                </Label>
                <Input
                  id="email-address"
                  type="email"
                  placeholder="your@email.com"
                  value={preferences.email_address}
                  onChange={(e) =>
                    setPreferences({ ...preferences, email_address: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
            )}
          </div>
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="sms">SMS Notifications</Label>
              <Switch
                id="sms"
                checked={preferences.notify_sms}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, notify_sms: checked })
                }
              />
            </div>
            {preferences.notify_sms && (
              <div className="pl-4">
                <Label htmlFor="phone" className="text-sm">
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1234567890"
                  value={preferences.phone_number}
                  onChange={(e) =>
                    setPreferences({ ...preferences, phone_number: e.target.value })
                  }
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Include country code (e.g., +1 for US)
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quiet Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Moon className="h-5 w-5" />
            Quiet Hours
          </CardTitle>
          <CardDescription>
            Disable non-critical notifications during specific hours
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="quiet-hours">Enable Quiet Hours</Label>
            <Switch
              id="quiet-hours"
              checked={preferences.quiet_hours_enabled}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, quiet_hours_enabled: checked })
              }
            />
          </div>
          {preferences.quiet_hours_enabled && (
            <>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start-time">Start Time</Label>
                  <Input
                    id="start-time"
                    type="time"
                    value={preferences.quiet_hours_start}
                    onChange={(e) =>
                      setPreferences({ ...preferences, quiet_hours_start: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-time">End Time</Label>
                  <Input
                    id="end-time"
                    type="time"
                    value={preferences.quiet_hours_end}
                    onChange={(e) =>
                      setPreferences({ ...preferences, quiet_hours_end: e.target.value })
                    }
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Critical alerts will still be delivered during quiet hours
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={savePreferences} disabled={saving} size="sm">
          <Save className="h-3 w-3 mr-1.5" />
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
}
